/**
 * Wizard "+ Campanha" (v10 Etapa 2) — dialog em 3 passos:
 * (1) Nome + Canal + Modelo (o modelo só é ESCOLHIDO aqui; os params são POR
 *     destinatário, então o preview mostra o body cru + contagem de params);
 * (2) Destinatários: textarea "um por linha: telefone;param1;param2" parseada
 *     no cliente (contagem + primeiros erros inline);
 * (3) Revisão: totais + amostra + confirmar (POST /whatsapp/campaigns).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { normalizeWaPhone, WA_CAMPAIGN_MAX_RECIPIENTS } from '@erp/shared';
import { Dialog, Spinner } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { whatsappChannelsOptions } from '@/features/whatsapp/whatsapp-queries';
import {
  templateFailureMessage,
  templatesOfNumber,
  whatsappTemplatesOptions,
  type WhatsappHsmTemplate,
} from '@/features/chats/chats-queries';
import { campaignErrorMessage, useCreateCampaign } from './campaigns-api';

// ── Parsing dos destinatários ───────────────────────────────────────────────

export interface ParsedRecipient {
  /** linha 1-based no textarea (para mensagens de erro) */
  line: number;
  /** telefone normalizado: só dígitos (DDI+DDD+número) */
  phone: string;
  params: string[];
}

export interface RecipientParseResult {
  recipients: ParsedRecipient[];
  /** mensagens pt-BR, uma por linha inválida (ordem do textarea) */
  errors: string[];
  /** linhas com telefone repetido (normalizado) — ignoradas, como na API */
  duplicates: number;
}

export function parseRecipients(text: string, paramCount: number): RecipientParseResult {
  const recipients: ParsedRecipient[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim();
    if (line === '') continue;
    const parts = line.split(';').map((p) => p.trim());
    // MESMA normalização da API (normalizeWaPhone do shared): sem '+' e
    // separadores, 8–15 dígitos — o que passa aqui passa no create.
    const phone = normalizeWaPhone(parts[0] ?? '');
    const params = parts.slice(1);
    if (phone === null) {
      errors.push(`Linha ${i + 1}: telefone inválido ("${parts[0] ?? ''}").`);
      continue;
    }
    if (params.length !== paramCount) {
      errors.push(
        `Linha ${i + 1}: o modelo pede ${paramCount} ${
          paramCount === 1 ? 'parâmetro' : 'parâmetros'
        }, a linha tem ${params.length}.`
      );
      continue;
    }
    if (params.some((p) => p === '')) {
      errors.push(`Linha ${i + 1}: parâmetro vazio.`);
      continue;
    }
    // MESMA deduplicação da API (primeira ocorrência vence) — assim o total
    // revisado bate com o totalRecipients da campanha criada.
    if (seen.has(phone)) {
      duplicates++;
      continue;
    }
    seen.add(phone);
    recipients.push({ line: i + 1, phone, params });
  }
  return { recipients, errors, duplicates };
}

// ── Wizard ──────────────────────────────────────────────────────────────────

const templateKey = (t: WhatsappHsmTemplate) => `${t.name} ${t.language}`;

export function CampaignWizardDialog({ onClose }: { onClose: () => void }) {
  const [channelsQ, templatesQ] = useQueries({
    queries: [whatsappChannelsOptions(), whatsappTemplatesOptions()],
  });
  const channels = (channelsQ.data ?? []).filter((c) => c.active);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [tplKey, setTplKey] = useState('');
  const [recipientsText, setRecipientsText] = useState('');

  // os modelos do NÚMERO da campanha (spec 025: cada conta tem os seus). Canal
  // da Blip não é opção de campanha (a API recusa com 422)
  const channel =
    channels.find((c) => String(c.id) === channelId && c.provider !== 'blip') ?? null;
  const { templates, failures } = channel
    ? templatesOfNumber(templatesQ.data, channel.phoneNumberId)
    : { templates: [], failures: [] };

  const template = templates.find((t) => templateKey(t) === tplKey) ?? null;
  const parsed = useMemo(
    () => (template ? parseRecipients(recipientsText, template.params) : null),
    [recipientsText, template]
  );

  const create = useCreateCampaign();

  const step1Ok = name.trim() !== '' && channel !== null && template !== null;
  const overCap = parsed !== null && parsed.recipients.length > WA_CAMPAIGN_MAX_RECIPIENTS;
  const step2Ok =
    parsed !== null && parsed.recipients.length > 0 && parsed.errors.length === 0 && !overCap;

  const submit = () => {
    if (!template || !step2Ok || !parsed) return;
    create.mutate(
      {
        name: name.trim(),
        channelId: Number(channelId),
        templateName: template.name,
        templateLanguage: template.language,
        recipients: parsed.recipients.map(({ phone, params }) => ({ phone, params })),
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Nova campanha" className="sm:max-w-2xl">
      {/* Indicador dos passos */}
      <ol className="mb-4 flex items-center gap-2 text-xs">
        {(
          [
            [1, 'Campanha'],
            [2, 'Destinatários'],
            [3, 'Revisão'],
          ] as const
        ).map(([n, label]) => (
          <li key={n} className="flex items-center gap-2">
            {n > 1 ? <span className="h-px w-6 bg-border" /> : null}
            <span
              className={cn(
                'flex items-center gap-1.5 font-medium',
                step === n ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full border text-[11px] tabular-nums',
                  step === n
                    ? 'border-primary bg-primary text-primary-foreground'
                    : step > n
                      ? 'border-primary text-primary'
                      : 'border-border'
                )}
              >
                {n}
              </span>
              {label}
            </span>
          </li>
        ))}
      </ol>

      {step === 1 ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cmp-name">
              Nome<span className="text-destructive">*</span>
            </Label>
            <Input
              id="cmp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Aviso de manutenção — agosto"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cmp-channel">
              Canal<span className="text-destructive">*</span>
            </Label>
            <Select
              id="cmp-channel"
              value={channelId}
              onChange={(e) => {
                setChannelId(e.target.value);
                setTplKey(''); // o modelo escolhido era do número anterior
              }}
              disabled={channelsQ.isPending || channels.length === 0}
            >
              <option value="">
                {channelsQ.isPending
                  ? 'Carregando canais…'
                  : channels.length === 0
                    ? 'Nenhum número ativo conectado'
                    : 'Selecione o número…'}
              </option>
              {channels.map((c) => (
                // campanha não sai pela Blip (marcaria cada destinatário no
                // roteador da PoolTrack): o número aparece, desabilitado, com o motivo
                <option key={c.id} value={c.id} disabled={c.provider === 'blip'}>
                  {c.displayPhone}
                  {c.provider === 'blip' ? ' — Blip: campanha não sai por este número' : ''}
                </option>
              ))}
            </Select>
            {channelsQ.isError ? (
              <p className="text-xs text-destructive" role="alert">
                Erro ao carregar os canais: {campaignErrorMessage(channelsQ.error)}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cmp-template">
              Modelo<span className="text-destructive">*</span>
            </Label>
            <Select
              id="cmp-template"
              value={tplKey}
              onChange={(e) => setTplKey(e.target.value)}
              disabled={!channel || templatesQ.isPending || templates.length === 0}
            >
              <option value="">
                {!channel
                  ? 'Escolha o canal primeiro'
                  : templatesQ.isPending
                    ? 'Carregando modelos…'
                    : templates.length === 0
                      ? failures.length > 0
                        ? 'Os modelos deste número não carregaram'
                        : 'Nenhum modelo disponível'
                      : 'Selecione um modelo…'}
              </option>
              {templates.map((t) => (
                <option key={templateKey(t)} value={templateKey(t)}>
                  {t.name} · {t.language}
                </option>
              ))}
            </Select>
            {templatesQ.isError ? (
              <p className="text-xs text-destructive" role="alert">
                Erro ao carregar os modelos: {campaignErrorMessage(templatesQ.error)}
              </p>
            ) : null}
            {failures.map((f) => (
              <p key={f.phoneNumberId} className="text-xs text-destructive" role="alert">
                {templateFailureMessage(f)}
              </p>
            ))}
          </div>
          {template ? (
            <div className="rounded-md border bg-muted/40 px-3 py-2.5">
              <p className="text-sm break-words whitespace-pre-wrap">{template.body}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {template.params === 0
                  ? 'Este modelo não tem parâmetros.'
                  : `${template.params} ${
                      template.params === 1 ? 'parâmetro' : 'parâmetros'
                    } por destinatário — os {{n}} acima são preenchidos por linha no próximo passo.`}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {step === 2 && template ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cmp-recipients">
              Destinatários<span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Um por linha, no formato{' '}
              <code className="rounded bg-muted px-1 py-0.5">
                telefone
                {Array.from({ length: template.params }, (_, i) => `;param${i + 1}`).join('')}
              </code>
              {' '}— telefone com DDI e DDD (ex.: 5541999998888).
            </p>
            <Textarea
              id="cmp-recipients"
              className="min-h-40 font-mono text-xs"
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              placeholder={`5541999998888${Array.from(
                { length: template.params },
                (_, i) => `;valor ${i + 1}`
              ).join('')}`}
              spellCheck={false}
            />
          </div>
          {parsed ? (
            <div className="space-y-1.5 text-sm">
              <p>
                <span className="font-semibold tabular-nums">{parsed.recipients.length}</span>{' '}
                {parsed.recipients.length === 1
                  ? 'destinatário válido'
                  : 'destinatários válidos'}
                {parsed.duplicates > 0 ? (
                  <span className="text-muted-foreground">
                    {' '}
                    · {parsed.duplicates}{' '}
                    {parsed.duplicates === 1 ? 'duplicata ignorada' : 'duplicatas ignoradas'}
                  </span>
                ) : null}
                {parsed.errors.length > 0 ? (
                  <span className="text-destructive">
                    {' '}
                    · {parsed.errors.length} {parsed.errors.length === 1 ? 'linha com erro' : 'linhas com erro'}
                  </span>
                ) : null}
              </p>
              {overCap ? (
                <p className="text-xs text-destructive" role="alert">
                  A lista passa do teto de {WA_CAMPAIGN_MAX_RECIPIENTS.toLocaleString('pt-BR')}{' '}
                  destinatários por campanha.
                </p>
              ) : null}
              {parsed.errors.length > 0 ? (
                <ul className="space-y-0.5 text-xs text-destructive" role="alert">
                  {parsed.errors.slice(0, 5).map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                  {parsed.errors.length > 5 ? (
                    <li className="text-muted-foreground">
                      …e mais {parsed.errors.length - 5}{' '}
                      {parsed.errors.length - 5 === 1 ? 'erro' : 'erros'}.
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {step === 3 && template && parsed ? (
        <div className="space-y-3 text-sm">
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <ReviewField label="Nome">{name.trim()}</ReviewField>
            <ReviewField label="Canal">
              {channels.find((c) => String(c.id) === channelId)?.displayPhone ?? channelId}
            </ReviewField>
            <ReviewField label="Modelo">
              {template.name} · {template.language}
            </ReviewField>
            <ReviewField label="Destinatários">
              <span className="font-semibold tabular-nums">{parsed.recipients.length}</span>
            </ReviewField>
          </div>
          {parsed.recipients.length > 0 ? (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted text-left">
                    <th className="h-8 px-3 font-semibold">Telefone</th>
                    <th className="h-8 px-3 font-semibold">Parâmetros</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.recipients.slice(0, 3).map((r) => (
                    <tr key={r.line} className="border-t border-divider">
                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">{r.phone}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {r.params.length > 0 ? r.params.join(' · ') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.recipients.length > 3 ? (
                <p className="border-t border-divider px-3 py-1.5 text-xs text-muted-foreground">
                  …e mais {parsed.recipients.length - 3} destinatários.
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            A campanha é criada como rascunho — o envio só começa quando você iniciá-la na lista.
          </p>
          {create.error ? (
            <p className="text-sm text-destructive" role="alert">
              {campaignErrorMessage(create.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Navegação */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => (step === 1 ? onClose() : setStep((s) => (s === 3 ? 2 : 1)))}
          disabled={create.isPending}
        >
          {step === 1 ? 'Cancelar' : 'Voltar'}
        </Button>
        {step < 3 ? (
          <Button
            type="button"
            size="sm"
            disabled={step === 1 ? !step1Ok : !step2Ok}
            onClick={() => setStep((s) => (s === 1 ? 2 : 3))}
          >
            Avançar
          </Button>
        ) : (
          <Button type="button" size="sm" disabled={create.isPending || !step2Ok} onClick={submit}>
            {create.isPending ? (
              <>
                <Spinner className="size-3.5 border-primary-foreground" /> Criando…
              </>
            ) : (
              'Criar campanha'
            )}
          </Button>
        )}
      </div>

    </Dialog>
  );
}

function ReviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 break-words">{children}</div>
    </div>
  );
}
